import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { getProductDiscountAnalysisApi } from "../../../services/manage-product/productService";
import "./DiscountAnalysisDrawer.css";

const formatLabel = (value) => String(value || "-")
  .toLowerCase()
  .split("_")
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(" ");

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isNaN(number)
    ? value
    : number.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const Section = ({ icon, title, children }) => (
  <section className="discount-analysis-card">
    <div className="discount-analysis-section-title">
      <i className={`fa ${icon} mr-2`} />{title}
    </div>
    {children}
  </section>
);

const EvidenceList = ({ items, icon, iconClass }) => (
  <div>
    {(items || []).map((item, index) => (
      <div className="discount-analysis-list-item" key={`${item}-${index}`}>
        <i className={`fa ${icon} ${iconClass} mr-2 mt-1`} />
        <div>{item}</div>
      </div>
    ))}
  </div>
);

const formatDiscount = (value) => {
  const formatted = formatNumber(value);
  return formatted === "-" ? formatted : `${formatted}%`;
};

const DiscountAnalysisDrawer = ({ productId, fallbackProductCode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setIsOpen(false);
    setAnalysis(null);
    setLoading(false);
    setError("");
  }, [productId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  const loadAnalysis = () => {
    if (!productId || loading || analysis) return;
    setLoading(true);
    setError("");
    getProductDiscountAnalysisApi(productId)
      .then(({ response }) => {
        const nextAnalysis = response?.data?.analysis || response?.analysis || null;
        setAnalysis(nextAnalysis);
      })
      .catch((requestError) => {
        setError(
          requestError.response?.data?.message
          || requestError.message
          || "Failed to load the discount analysis."
        );
      })
      .finally(() => setLoading(false));
  };

  const openDrawer = () => {
    setIsOpen(true);
    loadAnalysis();
  };

  const drawer = isOpen ? ReactDOM.createPortal(
    <>
      <div className="discount-analysis-backdrop" onClick={() => setIsOpen(false)} />
      <aside className="discount-analysis-drawer" role="dialog" aria-modal="true" aria-label="AI discount analysis">
        <header className="discount-analysis-header">
          <div className="d-flex align-items-start justify-content-between">
            <div>
              <div className="small mb-1" style={{ opacity: 0.8 }}>AI DISCOUNT ANALYSIS</div>
              <h4 className="mb-1 text-white">
                {fallbackProductCode || "Product"}
              </h4>
              {analysis?.confidence && (
                <span className="badge badge-light mt-1">{formatLabel(analysis.confidence)} confidence</span>
              )}
            </div>
            <button
              type="button"
              className="btn btn-link text-white p-0"
              onClick={() => setIsOpen(false)}
              aria-label="Close AI discount analysis"
              style={{ fontSize: "24px", lineHeight: 1 }}
            >
              &times;
            </button>
          </div>
        </header>

        <div className="discount-analysis-body">
          {loading && (
            <div className="text-center text-muted py-5">
              <i className="fa fa-spinner fa-spin fa-2x mb-3 d-block" />
              Analysing discount performance...
            </div>
          )}

          {!loading && error && (
            <div className="alert alert-danger">
              <div className="font-weight-bold mb-1">Analysis could not be loaded</div>
              <div className="mb-2">{error}</div>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={loadAnalysis}>Retry</button>
            </div>
          )}

          {!loading && !error && analysis && (
            <>
              <Section icon="fa-compass" title="Decision Summary">
                <div className="discount-analysis-comparison mb-3">
                  <div>
                    <div className="small text-muted">XGBoost discount</div>
                    <div className="discount-analysis-comparison-value">{formatDiscount(analysis.xgboost_discount)}</div>
                  </div>
                  <div className="discount-analysis-comparison-arrow"><i className="fa fa-long-arrow-right" aria-hidden="true" /></div>
                  <div>
                    <div className="small text-muted">LLM discount</div>
                    <div className="discount-analysis-comparison-value">{formatDiscount(analysis.llm_discount)}</div>
                  </div>
                </div>
                {analysis.decision && <div className="mb-3"><span className="badge badge-primary px-3 py-2">{formatLabel(analysis.decision)}</span></div>}
                {typeof analysis.xgboost_reasonable === "boolean" && (
                  <div className="mb-3">
                    <span className={`badge px-3 py-2 ${analysis.xgboost_reasonable ? "badge-success" : "badge-danger"}`}>
                      XGBoost discount: {analysis.xgboost_reasonable ? "Reasonable" : "Not reasonable"}
                    </span>
                  </div>
                )}
                <div className="font-weight-bold mb-1">Recommendation reason</div>
                <p className="mb-0">{analysis.reason || "-"}</p>
              </Section>

              <Section icon="fa-line-chart" title="Sales Prediction">
                <div className="discount-analysis-metric mb-3">
                  <div className="text-muted small">XGBoost predicted sales</div>
                  <div className="font-weight-bold" style={{ fontSize: "24px" }}>
                    {formatNumber(analysis.xgboost_predicted_sales)} <span className="small">units</span>
                  </div>
                  {analysis.prediction_verdict && (
                    <span className={`badge mt-2 px-3 py-2 ${analysis.prediction_verdict === "QUESTIONABLE" ? "badge-warning" : "badge-light"}`}>
                      Verdict: {formatLabel(analysis.prediction_verdict)}
                    </span>
                  )}
                </div>
                <p className="mb-0">{analysis.prediction_reason || "-"}</p>
              </Section>

              {Array.isArray(analysis.findings) && analysis.findings.length > 0 && (
                <Section icon="fa-search" title="Key Findings">
                  <EvidenceList items={analysis.findings} icon="fa-info-circle" iconClass="text-info" />
                </Section>
              )}
              {Array.isArray(analysis.actions) && analysis.actions.length > 0 && (
                <Section icon="fa-check-square-o" title="Recommended Actions">
                  <EvidenceList items={analysis.actions} icon="fa-check-circle" iconClass="text-success" />
                </Section>
              )}
              {Array.isArray(analysis.risks) && analysis.risks.length > 0 && (
                <Section icon="fa-exclamation-triangle" title="Risks">
                  <EvidenceList items={analysis.risks} icon="fa-exclamation-circle" iconClass="text-danger" />
                </Section>
              )}
            </>
          )}
        </div>
      </aside>
    </>,
    document.body
  ) : null;

  return (
    <>
      <button
        type="button"
        className="btn btn-sm ml-2 text-white"
        onClick={openDrawer}
        title="View AI discount analysis"
        style={{ backgroundColor: "#514a9d", borderRadius: "999px", padding: "4px 10px" }}
      >
        <i className="fa fa-lightbulb-o mr-1" />AI Analysis
      </button>
      {drawer}
    </>
  );
};

export default DiscountAnalysisDrawer;
